import { FC, useCallback, useEffect, useState } from 'react';
import { Box, Grid, Radio, FormControlLabel, Paper, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { setOrderDiscount } from '@store/slices/order';
import { CartItem } from './CartItem';
import { toCurrency } from '@helpers';
import usePrevious from '@hooks/usePrevious';

type Props = {
  deliveryFee?: number;
  setDeliveryFee: (deliveryFeeChecked: boolean) => void;
};

export const Cart: FC<Props> = ({ deliveryFee, setDeliveryFee }) => {
  const order = useAppSelector((state) => state.order.value);
  const dispatch = useAppDispatch();
  const [deliveryOption, setDeliveryOption] = useState(1);
  const [cartDiscount, setCartDiscount] = useState<number | undefined>();
  const previousPaymentMethod: any = usePrevious(order.selectedPaymentMethod);

  const handleDiscountChange = useCallback(
    (newValue: string) => {
      const valueAsNumber = Number(newValue);
      const newDiscount = valueAsNumber > 100 ? 100 : valueAsNumber < 0 ? 0 : valueAsNumber;

      setCartDiscount(newDiscount);
      dispatch(setOrderDiscount(newDiscount / 100));
    },
    [dispatch],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeliveryOption(Number(event.target.value));

    if (Number(event.target.value) === 0) {
      setDeliveryFee(true);
    } else {
      setDeliveryFee(false);
    }
  };

  const totalOrderValue = toCurrency(
    order.totalPrice * (1 - (order.orderDiscountPercentage || 0)) + ((!deliveryOption ? 0 : deliveryFee) ?? 0),
  );

  useEffect(() => {
    if (previousPaymentMethod?.id === 'noCharge' && order.selectedPaymentMethod?.id !== 'noCharge') {
      handleDiscountChange('0');
    }

    if (order.selectedPaymentMethod?.id === 'noCharge') {
      setDeliveryOption(0);
      setCartDiscount(undefined);
    }
  }, [handleDiscountChange, order.selectedPaymentMethod, previousPaymentMethod?.id]);

  return (
    <Box py={2} data-testid="order-cart">
      <Typography variant="h5" mx={1}>
        Cart
      </Typography>
      <Paper elevation={2}>
        {order.products?.length ? (
          <>
            <Grid container p={2} my={1}>
              <Grid item xs={12}>
                <RadioGroup onChange={handleChange} value={deliveryOption}>
                  <FormControlLabel control={<Radio />} label="Free Delivery" value={0} />
                  <FormControlLabel
                    control={<Radio />}
                    label="Delivery Fee"
                    value={1}
                    disabled={order.selectedPaymentMethod?.id === 'noCharge'}
                  />
                </RadioGroup>
              </Grid>
            </Grid>
            <Stack spacing={4} p={2}>
              {order.products?.map(({ productId, quantity }) => (
                <CartItem key={productId} productId={productId} quantity={quantity} />
              ))}
            </Stack>
            <Grid container data-testid="subtotal-and-discounts" p={2} spacing={2}>
              <Grid item xs={12} textAlign="right">
                <TextField
                  type="number"
                  data-testid="discount-field"
                  size="medium"
                  value={cartDiscount}
                  onChange={({ target: { value } }) => handleDiscountChange(value)}
                  InputLabelProps={{ shrink: true }}
                  label="Cart Discount %"
                  disabled={order.selectedPaymentMethod?.id === 'noCharge'}
                />
              </Grid>
              <Grid item xs={12} textAlign="right">
                <Typography variant="subtitle1">Subtotal: {toCurrency(order.totalPrice)}</Typography>
              </Grid>
              {order?.orderDiscountPercentage !== undefined && order.orderDiscountPercentage > 0 && (
                <Grid item xs={12} textAlign="right">
                  <Typography variant="subtitle1">Discount: {toCurrency(order.totalPrice * order?.orderDiscountPercentage)}</Typography>
                </Grid>
              )}
              {deliveryOption === 1 && (
                <Grid item xs={12} textAlign="right">
                  <Typography variant="subtitle1" data-testid="delivery-fee-field">
                    Includes delivery fee: ${deliveryFee}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} textAlign="right">
                <Typography variant="h6" data-testid="total-price">
                  Total: {totalOrderValue}
                </Typography>
              </Grid>
            </Grid>
          </>
        ) : (
          <Typography variant="h6" data-testid="empty-cart" textAlign="center">
            Cart is empty
          </Typography>
        )}
      </Paper>
    </Box>
  );
};
